/**
 * 多角色（角色名）相关读取与判断的统一入口。
 *
 * 背景：系统已从「单角色」迁移至「多角色」，登录响应里同时下发：
 *   - obj.roleName  : 主角色名（旧字段，向后兼容）
 *   - obj.roleNames : 当前账号的全部角色名数组
 *
 * 前端登录后将 roleNames 以 JSON 字符串写入 sessionStorage('roleNames')，
 * 旧字段 sessionStorage('role') 同时保留写入，仍存放主角色名。
 *
 * 业务页面统一通过本工具读取，不要再直接 sessionStorage.getItem('role')。
 */

import Vue from "vue";

const ROLE_NAMES_KEY = "roleNames";
const ROLE_KEY = "role";

/**
 * 获取当前登录用户的全部角色名数组。
 * - 优先读 sessionStorage('roleNames')（多角色，迁移后写入）
 * - 兼容只有旧字段 sessionStorage('role') 的回滚场景
 */
export function getCurrentRoles() {
  const raw = sessionStorage.getItem(ROLE_NAMES_KEY);
  if (raw) {
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        return arr.filter(r => typeof r === "string" && r.length > 0);
      }
    } catch (e) {
      // ignore parse error, fallback to single role
    }
  }
  const single = sessionStorage.getItem(ROLE_KEY);
  return single ? [single] : [];
}

/** 判断是否拥有指定角色名。 */
export function hasRole(roleName) {
  if (!roleName) return false;
  return getCurrentRoles().indexOf(roleName) !== -1;
}

/** 判断是否拥有列表中任意一个角色名。 */
export function hasAnyRole(roleNames) {
  if (!Array.isArray(roleNames) || roleNames.length === 0) return false;
  const owned = getCurrentRoles();
  return roleNames.some(r => owned.indexOf(r) !== -1);
}

/** 取首角色名（向后兼容旧字段语义；仅在确实需要单值时使用）。 */
export function getPrimaryRole() {
  const arr = getCurrentRoles();
  return arr.length > 0 ? arr[0] : "";
}

/**
 * 弹窗让用户从给定角色集合中选择一个。
 * 返回 Promise<string>，用户取消则 reject(new Error('cancel'))。
 */
export function selectRoleFromOwned(roles, title) {
  return new Promise((resolve, reject) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const vm = new Vue({
      data() {
        return {
          visible: true,
          selected: roles[0] || "",
          roles
        };
      },
      methods: {
        cleanup() {
          setTimeout(() => {
            this.$destroy();
            if (container.parentNode) container.parentNode.removeChild(container);
          }, 300);
        },
        onConfirm() {
          if (!this.selected) return;
          const v = this.selected;
          this.visible = false;
          resolve(v);
        },
        onCancel() {
          this.visible = false;
          reject(new Error("cancel"));
        }
      },
      mounted() {
        this.$watch("visible", v => {
          if (!v) this.cleanup();
        });
      },
      render(h) {
        return h(
          "el-dialog",
          {
            props: {
              title: title || "请选择发布附言的角色身份",
              visible: this.visible,
              width: "360px",
              appendToBody: true,
              closeOnClickModal: false,
              closeOnPressEscape: false
            },
            on: {
              "update:visible": v => {
                this.visible = v;
                if (!v) reject(new Error("cancel"));
              }
            }
          },
          [
            h(
              "el-radio-group",
              {
                props: { value: this.selected },
                on: { input: v => { this.selected = v; } }
              },
              this.roles.map(r =>
                h(
                  "el-radio",
                  {
                    key: r,
                    props: { label: r },
                    style: "display:block;margin:8px 0;"
                  },
                  r
                )
              )
            ),
            h("div", { slot: "footer" }, [
              h("el-button", { on: { click: this.onCancel } }, "取消"),
              h(
                "el-button",
                { props: { type: "primary" }, on: { click: this.onConfirm } },
                "确定"
              )
            ])
          ]
        );
      }
    }).$mount(container);
    void vm;
  });
}

/**
 * 计算附言提交时使用的角色名。
 * 规则：
 *   - isOwner=true（当前任务持有人）：
 *       1. currentTaskRoleName ∈ 用户角色集合 → 直接返回（处理人本节点角色）
 *       2. 单角色 → 返回该角色
 *       3. 多角色未命中 → 弹窗选择
 *       4. 角色集合为空 → 回退 sessionStorage('role')
 *   - isOwner=false（查看人 / 非持有人）：
 *       忽略 currentTaskRoleName（避免与查看人自身角色巧合命中），
 *       1. 单角色 → 返回该角色
 *       2. 多角色 → 弹窗选择
 *       3. 角色集合为空 → 回退 sessionStorage('role')
 *
 * 返回 Promise<string>，用户取消选择时 reject(new Error('cancel'))。
 *
 * @param {string} currentTaskRoleName 当前任务节点角色名
 * @param {{isOwner?: boolean}} [options] 调用方上下文。默认 isOwner=true 兼容旧调用。
 */
export function resolvePostRole(currentTaskRoleName, options) {
  const isOwner = !options || options.isOwner !== false;
  const owned = getCurrentRoles();
  if (isOwner && currentTaskRoleName && owned.indexOf(currentTaskRoleName) !== -1) {
    return Promise.resolve(currentTaskRoleName);
  }
  if (owned.length === 1) {
    return Promise.resolve(owned[0]);
  }
  if (owned.length > 1) {
    return selectRoleFromOwned(owned);
  }
  return Promise.resolve(sessionStorage.getItem(ROLE_KEY) || "");
}
